/**
 * @file `CampaignsPanel` — the `/emails/campaigns` route: a newest-first
 *       list of sent campaigns with subject search, a "needs attention"
 *       filter, and client-side pagination.
 * @module @labcraft/admin/ui/emails/campaigns-panel
 *
 * Read-only. Each row links to the per-campaign delivery detail. "Needs
 * attention" flags campaigns with failures (`failedCount > 0`) until the
 * per-state health aggregates land (Part 12 switches it to the real
 * worst-state). The page resets to 1 whenever a filter changes.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
} from 'react'
import { Link } from 'react-router'
import { useCampaigns, usePagedList } from '../hooks/index.ts'
import { MiniDeliveryBar, worstStateOf } from './mini_delivery_bar.tsx'
import { Pager } from '../kernel/pager.tsx'

const COLUMNS = [
  'Subject',
  'Recipients',
  'Delivery',
  'Sent',
  'Failed',
  'Sent at',
] as const

/** `2026-05-21T12:00:00Z` → `May 21, 2026`; invalid → em dash. */
function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** Render the campaigns list route. */
export function CampaignsPanel(): JSX.Element {
  const result = useCampaigns()
  const all = result.data?.campaigns ?? null
  const error = result.isError
    ? result.error.message || 'Could not load campaigns.'
    : null

  const [q, setQ] = useState('')
  const [needsAttention, setNeedsAttention] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const attentionCount = useMemo(
    () => (all ?? []).filter(c => worstStateOf(c) === 'bad').length,
    [all],
  )

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (all ?? []).filter(
      c =>
        (!needsAttention || worstStateOf(c) === 'bad') &&
        (needle === '' || c.subject.toLowerCase().includes(needle)),
    )
  }, [all, q, needsAttention])

  const { page, setPage, resetPage, pageSize, rowHeight, offset, pageCount } =
    usePagedList(scrollRef, filtered.length)

  // Reset to the first page whenever a filter changes.
  useEffect(() => {
    resetPage()
  }, [q, needsAttention, resetPage])

  const paged = filtered.slice(offset, offset + pageSize)

  return (
    <div className="panel emails-panel emails-panel--paged">
      <header className="panel-head">
        <div>
          <div className="panel-head__title-row">
            <h1 className="panel-head__title">Campaigns</h1>
            <span className="panel-head__count">
              {all ? `${filtered.length} shown` : '—'}
            </span>
          </div>
          <p className="panel-head__sub">
            // every broadcast, newest first. click a row for delivery detail.
          </p>
        </div>
      </header>

      {error !== null && (
        <div className="panel-banner panel-banner--error">
          <b>Could not load campaigns.</b> {error}
        </div>
      )}

      <div className="emails-subbar">
        <input
          className="lc-input emails-search"
          type="search"
          placeholder="search a subject…"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <div className="chips" role="group" aria-label="Filter campaigns">
          <button
            type="button"
            className="chip"
            aria-pressed={!needsAttention}
            onClick={() => setNeedsAttention(false)}>
            All
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={needsAttention}
            onClick={() => setNeedsAttention(true)}>
            Needs attention
            <span className="chip__count">{attentionCount}</span>
          </button>
        </div>
      </div>

      <div className="paged-list">
        <div
          className="table-scroll"
          ref={scrollRef}
          style={
            rowHeight > 0
              ? ({ '--row-h': `${rowHeight}px` } as CSSProperties)
              : undefined
          }>
          <table className="table">
            <thead>
              <tr>
                {COLUMNS.map(c => (
                  <th key={c}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.isPending && all === null && (
                <tr>
                  <td className="empty-row" colSpan={COLUMNS.length}>
                    // loading…
                  </td>
                </tr>
              )}
              {all && filtered.length === 0 && (
                <tr>
                  <td className="empty-row" colSpan={COLUMNS.length}>
                    // no campaigns match.
                  </td>
                </tr>
              )}
              {paged.map(c => (
                <tr
                  key={c.id}
                  className={worstStateOf(c) === 'bad' ? 'row-flag' : ''}>
                  <td className="cell-subject">
                    <Link to={`/emails/campaigns/${c.id}`}>
                      {c.subject || '(no subject)'}
                    </Link>
                  </td>
                  <td className="cell-meta">{c.recipientCount}</td>
                  <td>
                    <MiniDeliveryBar campaign={c} />
                  </td>
                  <td className="cell-meta">{c.sentCount}</td>
                  <td
                    className={
                      c.failedCount > 0 ? 'cell-meta cell-bad' : 'cell-meta'
                    }>
                    {c.failedCount}
                  </td>
                  <td className="cell-meta">{formatDate(c.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="paged-foot">
          <Pager
            page={page}
            pageCount={pageCount}
            onPage={setPage}
            total={all ? filtered.length : undefined}
          />
        </div>
      </div>
    </div>
  )
}
